import { useEffect, useState } from "react";
import { FileSignature, KeyRound, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";

// Forces (1) temp-password change and (2) onboarding consent signing for
// facility staff (basePath="/institution") and care providers (basePath="/provider").
export const StaffConsentGate = ({ basePath, needsPassword, onDone }) => {
    const [pwDone, setPwDone] = useState(false);
    const showPassword = needsPassword && !pwDone;
    return (
        <div className="fixed inset-0 bg-phoenix-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            {showPassword
                ? <PasswordStep basePath={basePath} onChanged={() => setPwDone(true)} />
                : <ConsentStep basePath={basePath} onDone={onDone} />}
        </div>
    );
};

function PasswordStep({ basePath, onChanged }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const submit = async () => {
        setError("");
        if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
        if (next !== confirm) { setError("New passwords do not match."); return; }
        setSaving(true);
        try {
            await api.post(`${basePath}/staff/change-password`, { current_password: current, new_password: next });
            onChanged();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not change password. Try again.");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="force-password-gate" className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8 space-y-4">
            <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-phoenix-navy text-white flex items-center justify-center"><KeyRound className="w-5 h-5" /></span>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-phoenix-orange">Security required</div>
                    <h2 className="font-display font-bold text-xl text-phoenix-navy">Set your new password</h2>
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                You signed in with a temporary password. Choose a new password (minimum 8 characters) to continue.
            </p>
            {error && <p data-testid="password-gate-error" className="text-sm text-destructive">{error}</p>}
            <input data-testid="password-gate-current" type="password" className={inputCls} placeholder="Temporary password"
                value={current} onChange={(e) => setCurrent(e.target.value)} />
            <input data-testid="password-gate-new" type="password" className={inputCls} placeholder="New password"
                value={next} onChange={(e) => setNext(e.target.value)} />
            <input data-testid="password-gate-confirm" type="password" className={inputCls} placeholder="Confirm new password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            <button data-testid="password-gate-submit" disabled={saving || !current || next.length < 8 || next !== confirm}
                onClick={submit}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Update password & continue"}
            </button>
        </div>
    );
}

function ConsentStep({ basePath, onDone }) {
    const [data, setData] = useState(null);
    const [checked, setChecked] = useState({});
    const [signature, setSignature] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`${basePath}/staff-consent`).then((r) => {
            if (r.data.accepted) { onDone(); return; }
            setData(r.data);
        }).catch(() => setError("Could not load consents. Refresh and try again."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basePath]);

    const items = data?.items || [];
    const allChecked = items.length > 0 && items.every((i) => checked[i.id]);

    const sign = async () => {
        setError("");
        setSaving(true);
        try {
            await api.post(`${basePath}/staff-consent`, {
                items: items.map((i) => ({ id: i.id, label: i.label, agreed: !!checked[i.id] })),
                signature_name: signature.trim(),
            });
            onDone();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not record consent. Try again.");
        } finally { setSaving(false); }
    };

    if (!data) {
        return (
            <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
                {error ? <p className="text-sm text-destructive">{error}</p>
                    : <Loader2 className="w-6 h-6 animate-spin text-phoenix-navy mx-auto" />}
            </div>
        );
    }

    return (
        <div data-testid="staff-consent-gate" className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6 sm:p-8 space-y-4">
            <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-phoenix-navy text-white flex items-center justify-center"><FileSignature className="w-5 h-5" /></span>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-phoenix-orange">Onboarding required</div>
                    <h2 className="font-display font-bold text-xl text-phoenix-navy">
                        {data.audience === "provider" ? "Care Provider Consents" : "Facility Consents"}
                    </h2>
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                Before using Blue Phoenix you must review and agree to each item below. Your signed consent is
                stored as a timestamped audit record.
            </p>

            <div className="space-y-3">
                {items.map((it) => (
                    <label key={it.id} data-testid={`staff-consent-item-${it.id}`}
                        className="block bg-secondary/30 border border-border rounded-lg p-4 cursor-pointer hover:border-phoenix-navy/40 transition-colors">
                        <div className="flex items-start gap-3">
                            <input data-testid={`staff-consent-check-${it.id}`} type="checkbox" className="mt-1 rounded"
                                checked={!!checked[it.id]}
                                onChange={(e) => setChecked((c) => ({ ...c, [it.id]: e.target.checked }))} />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-phoenix-navy">{it.label}</div>
                                <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap max-h-48 overflow-y-auto">{it.detail}</div>
                            </div>
                        </div>
                    </label>
                ))}
            </div>

            {error && <p data-testid="staff-consent-error" className="text-sm text-destructive">{error}</p>}
            <input data-testid="staff-consent-signature" className={inputCls} placeholder="Full legal name (electronic signature)"
                value={signature} onChange={(e) => setSignature(e.target.value)} />
            <button data-testid="staff-consent-submit" disabled={!allChecked || signature.trim().length < 2 || saving} onClick={sign}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "I agree — sign & continue"}
            </button>
        </div>
    );
}
